import { Link } from "react-router-dom";
import { Wrench, ChevronLeft } from "lucide-react";

export default function Privacy() {
  return (
    <div className="min-h-screen w-full bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-blue-700 flex items-center justify-center text-white">
            <Wrench className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading font-extrabold text-xl text-slate-900">
              Bell Appliance Repair
            </div>
            <div className="text-xs text-slate-500 uppercase tracking-wider">
              Privacy Policy
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6 text-slate-700 leading-relaxed">
        <Link to="/book" className="inline-flex items-center gap-1 text-sm text-blue-700 hover:underline">
          <ChevronLeft className="w-4 h-4" /> Back to booking
        </Link>

        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900">
          Privacy Policy
        </h1>
        <p className="text-xs text-slate-500 uppercase tracking-wider">
          Effective: February 2026
        </p>

        <section className="space-y-3">
          <p>
            Bell Appliance Repair LLC (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) respects your privacy. This
            Privacy Policy explains what information we collect, how we use it, and the choices
            you have.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-heading text-xl font-bold text-slate-900">1. Information We Collect</h2>
          <p>
            When you book a service appointment with us — through our online booking form, by
            phone, or by email — we collect information you provide, including:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Name (first and last)</li>
            <li>Phone number</li>
            <li>Email address (optional)</li>
            <li>Service address</li>
            <li>Appliance details (type, manufacturer, model, problem description)</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-heading text-xl font-bold text-slate-900">
            2. How We Use Your Information
          </h2>
          <p>We use the information you provide only to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Schedule and confirm your repair appointment</li>
            <li>Communicate with you about your appointment (SMS text messages, email, or phone)</li>
            <li>Dispatch a technician to your address</li>
            <li>Maintain a record of the service performed (for warranty and support)</li>
            <li>Comply with legal or regulatory obligations</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-heading text-xl font-bold text-slate-900">
            3. SMS / Text Messaging Program
          </h2>
          <p>
            <strong>No sharing of mobile information with third parties or affiliates:</strong>{" "}
            Mobile information and messaging consent will not be shared with third parties or
            affiliates for marketing or promotional purposes. All the above categories exclude
            text messaging originator opt-in data and consent; this information will not be
            shared with any third parties or affiliates.
          </p>
          <p>
            Mobile phone numbers you provide to Bell Appliance Repair for the purpose of
            receiving SMS messages are considered private information and will{" "}
            <strong>never</strong> be sold, rented, or shared with any third party or affiliate
            for marketing or promotional purposes.
          </p>
          <p>
            <strong>Consent (opt-in required):</strong> SMS text messaging is{" "}
            <strong>strictly opt-in and is not required</strong> to book service or use our
            website. To receive SMS updates you must actively check the SMS consent
            checkbox on our online booking form at{" "}
            <Link to="/book" className="text-blue-700 hover:underline">
              /book
            </Link>
            . The checkbox is <strong>unchecked by default</strong> and no SMS message will
            be sent unless you explicitly opt in. Customers who prefer not to receive SMS
            will be contacted by phone or email instead. You may also provide verbal or
            written consent directly to our staff, which we record on your customer profile
            with a timestamp and source.
          </p>
          <p>
            When you opt in, you consent to receive transactional SMS messages from Bell
            Appliance Repair related to your appointment — booking confirmations,
            appointment reminders, technician arrival notifications, and other transactional
            messages related to the service you requested.
          </p>
          <p>
            <strong>Message frequency:</strong> You may receive up to <strong>4 messages per
            appointment</strong> (a booking confirmation, an appointment reminder, an on-the-way
            notification, and occasional follow-ups related to your service). We do not send
            marketing or promotional text messages under this program.
          </p>
          <p>
            <strong>Message and data rates may apply.</strong> Your carrier&apos;s standard SMS
            rates apply to any messages you send or receive. Bell Appliance Repair does not
            charge you separately for these text messages.
          </p>
          <p>
            <strong>Opting out:</strong> You may opt out of receiving text messages at any time
            by replying <strong>STOP</strong> to any message you receive from us. After you opt
            out, you will receive a confirmation and no further messages will be sent. You can
            reply <strong>HELP</strong> to any message for support, or contact us directly (see
            below).
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-heading text-xl font-bold text-slate-900">
            4. How We Share Information
          </h2>
          <p>We share your information only with:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Our technicians (to perform the service you requested)</li>
            <li>
              Service providers we use to operate our business (SMS/email delivery,
              cloud hosting) — bound by confidentiality obligations
            </li>
            <li>Government agencies or courts when required by law</li>
          </ul>
          <p>
            We do <strong>not</strong> sell your personal information. Mobile information and
            messaging consent will not be shared with third parties or affiliates for marketing
            or promotional purposes.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-heading text-xl font-bold text-slate-900">5. Data Retention</h2>
          <p>
            We retain your information for as long as needed to provide service, honor warranty
            obligations, and comply with tax and legal record-keeping requirements. You may
            request deletion of your data by contacting us (see below).
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-heading text-xl font-bold text-slate-900">6. Your Choices</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Request a copy of the information we hold about you</li>
            <li>Ask us to correct or delete your information</li>
            <li>Opt out of SMS by replying STOP</li>
            <li>Unsubscribe from emails using the unsubscribe link at the bottom</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-heading text-xl font-bold text-slate-900">7. Contact Us</h2>
          <p>Questions about this Privacy Policy or your data:</p>
          <p>
            <strong>Bell Appliance Repair LLC</strong><br />
            2216 Rosedell Place<br />
            Fort Washington, MD 20744<br />
            Email:{" "}
            <a href="mailto:bellappliancerepair@gmail.com" className="text-blue-700 hover:underline">
              bellappliancerepair@gmail.com
            </a>
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="font-heading text-xl font-bold text-slate-900">8. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. Changes take effect when posted
            on this page. The &quot;Effective&quot; date at the top indicates the most recent
            update.
          </p>
        </section>

        <footer className="pt-8 border-t border-slate-200 text-xs text-slate-400">
          © {new Date().getFullYear()} Bell Appliance Repair LLC. All rights reserved.{" "}
          <Link to="/terms" className="text-blue-700 hover:underline">
            Terms of Service
          </Link>
        </footer>
      </main>
    </div>
  );
}
